import React, { useState } from 'react';
import { useSupabase } from '../../contexts/SupabaseContext';
import { Send, Users, CheckCircle } from 'lucide-react';

const CommunicationPage: React.FC = () => {
  const { supabase } = useSupabase();
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetAudience, setTargetAudience] = useState('all');
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSendNotification = async () => {
    if (!title || !message) {
      alert('Por favor, preencha título e mensagem');
      return;
    }

    try {
      setSending(true);
      setSuccess(false);

      let query = supabase.from('profiles').select('id');

      if (targetAudience === 'free') {
        query = query.eq('plan', 'free');
      } else if (targetAudience === 'paid') {
        query = query.in('plan', ['monthly', 'annual']);
      } else if (targetAudience === 'trial') {
        query = query.eq('status', 'trial');
      }

      const { data: users, error: usersError } = await query;

      if (usersError) throw usersError;

      if (!users || users.length === 0) {
        alert('Nenhum usuário encontrado para esse público');
        return;
      }

      const notifications = users.map(user => ({
        user_id: user.id,
        title,
        message,
        type: 'system',
        created_at: new Date().toISOString(),
        read: false,
      }));

      const { error: insertError } = await supabase
        .from('notifications')
        .insert(notifications);

      if (insertError) throw insertError;

      setSuccess(true);
      setTitle('');
      setMessage('');
      setTargetAudience('all');

      setTimeout(() => setSuccess(false), 3000);
    } catch (error) {
      console.error('Error sending notification:', error);
      alert('Erro ao enviar notificação');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-white">Comunicação com Usuários</h1>

      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-6">Enviar Notificação</h2>

        {success && (
          <div className="mb-6 p-4 bg-green-500/20 border border-green-500 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-5 h-5 text-green-400" />
            <p className="text-green-400">Notificação enviada com sucesso!</p>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-slate-300 mb-2">Público Alvo</label>
            <select
              value={targetAudience}
              onChange={(e) => setTargetAudience(e.target.value)}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">Todos os usuários</option>
              <option value="free">Apenas plano gratuito</option>
              <option value="paid">Apenas assinantes</option>
              <option value="trial">Apenas em trial</option>
            </select>
          </div>

          <div>
            <label className="block text-slate-300 mb-2">Título</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Digite o título da notificação"
              className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500"
            />
          </div>

          <div>
            <label className="block text-slate-300 mb-2">Mensagem</label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Digite a mensagem da notificação"
              rows={6}
              className="w-full px-4 py-2 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-amber-500 resize-none"
            />
          </div>

          <button
            onClick={handleSendNotification}
            disabled={sending || !title || !message}
            className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-amber-500 hover:bg-amber-600 disabled:bg-slate-700 disabled:text-slate-500 text-white rounded-lg transition-colors font-medium"
          >
            <Send className="w-5 h-5" />
            {sending ? 'Enviando...' : 'Enviar Notificação'}
          </button>
        </div>
      </div>

      <div className="bg-slate-800 rounded-lg border border-slate-700 p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Templates de Mensagens</h2>
        <div className="space-y-3">
          <button
            onClick={() => {
              setTitle('Bem-vindo ao Presida.Club!');
              setMessage('Olá! Estamos muito felizes em ter você conosco. Aproveite todas as funcionalidades da plataforma!');
            }}
            className="w-full text-left px-4 py-3 bg-slate-900 hover:bg-slate-700 rounded-lg text-white transition-colors"
          >
            <p className="font-medium">Boas-vindas</p>
            <p className="text-sm text-slate-400">Mensagem de boas-vindas para novos usuários</p>
          </button>

          <button
            onClick={() => {
              setTitle('Novos Recursos Disponíveis');
              setMessage('Confira as novidades que acabamos de lançar! Acesse o menu de ajuda para saber mais.');
            }}
            className="w-full text-left px-4 py-3 bg-slate-900 hover:bg-slate-700 rounded-lg text-white transition-colors"
          >
            <p className="font-medium">Novidades</p>
            <p className="text-sm text-slate-400">Anunciar novos recursos</p>
          </button>

          <button
            onClick={() => {
              setTitle('Seu Trial Está Terminando');
              setMessage('Seu período de trial está chegando ao fim. Assine agora e continue aproveitando todos os recursos!');
            }}
            className="w-full text-left px-4 py-3 bg-slate-900 hover:bg-slate-700 rounded-lg text-white transition-colors"
          >
            <p className="font-medium">Lembrete de Trial</p>
            <p className="text-sm text-slate-400">Lembrar usuários sobre fim do trial</p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CommunicationPage;
